import { Link } from 'react-router-dom'
import { Car } from 'lucide-react'
import { assetUrl } from '../../lib/http'
import { pkr } from '../../utils/format'
import { EmptyAccount, Loading, Problem } from '../../components/account/AccountStates'
import { useMyVehicles } from '../../hooks/useAccountQueries'

/* The cars now in this person's name.

   Not what they are looking at — what they own. The figure on the card is
   the immutable amount agreed on its sale, never today's live variant quote.
   Showroom cost and realised margin remain console-only. */

export function AccountCars() {
  const query = useMyVehicles()

  if (query.isPending) return <Loading label="Loading your cars" />
  if (query.isError) return <Problem onRetry={() => void query.refetch()} />

  const cars = query.data ?? []

  if (cars.length === 0) {
    return (
      <EmptyAccount
        icon={<Car className="size-5" strokeWidth={1.8} />}
        title="No cars in your name yet"
        body="Once a car is transferred to you it appears here, with its plate number and the paint it left on."
        action={
          <Link to="/home#cars" className="text-[13.5px] font-medium text-lime hover:text-lime-lift">
            See what is on the floor
          </Link>
        }
      />
    )
  }

  return (
    <ul className="grid gap-4 sm:grid-cols-2">
      {cars.map((car) => (
        <li key={car.id}>
          <article className="h-full overflow-hidden rounded-2xl border border-line bg-raised/60">
            <span className="block aspect-[16/10] w-full overflow-hidden bg-surface">
              {car.image ? (
                <img
                  src={assetUrl(car.image)}
                  alt={car.title}
                  loading="lazy"
                  className="size-full object-contain p-3"
                />
              ) : null}
            </span>

            <div className="p-5">
              <h3 className="font-display text-[17px] font-semibold tracking-[-0.02em] text-platinum">
                {car.title}
              </h3>
              <p className="mt-1 flex items-center gap-2 text-[13px] text-dim">
                {car.colourHex ? (
                  <span
                    aria-hidden="true"
                    style={{ backgroundColor: car.colourHex }}
                    className="size-3 shrink-0 rounded-full ring-1 ring-inset ring-white/20"
                  />
                ) : null}
                {car.year}
                {car.colour ? ` · ${car.colour}` : ''}
              </p>

              <dl className="mt-4 flex flex-col gap-1.5 border-t border-line pt-4 text-[13px]">
                {car.plateNumber ? (
                  <div className="flex justify-between gap-4">
                    <dt className="text-steel">Plate number</dt>
                    <dd className="truncate font-mono text-platinum">{car.plateNumber}</dd>
                  </div>
                ) : null}
                {car.soldPrice && car.soldPrice > 0 ? (
                  <div className="flex justify-between gap-4">
                    <dt className="text-steel">Agreed at</dt>
                    <dd className="text-platinum tabular-nums">
                      PKR {pkr.format(car.soldPrice)}
                    </dd>
                  </div>
                ) : null}
              </dl>
            </div>
          </article>
        </li>
      ))}
    </ul>
  )
}

export default AccountCars
